import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api } from '@/lib/api';
import { Section } from '@/components/Section';
import { KpiCard } from '@/components/KpiCard';
import { cn } from '@/lib/cn';

export function SystemPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const systemQuery = useQuery({
    queryKey: ['system', slug],
    queryFn: () => api.getSystem(slug!, () => getToken()),
    refetchInterval: 30_000,
  });

  if (systemQuery.isPending) {
    return (
      <div className="flex items-center gap-2 text-ducens-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
      </div>
    );
  }

  if (!systemQuery.data) return null;

  const data = systemQuery.data;
  const openCircuits = data.circuits.filter((c) => c.state !== 'closed');
  const budgetPct = (data.budget.daily_used_usd / data.budget.daily_max_usd) * 100;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Système</h1>
        <p className="text-sm text-ducens-muted mt-1">
          Santé technique : circuits, budget, erreurs récentes.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <KpiCard
          label="Budget IA jour"
          value={data.budget.daily_used_usd.toFixed(2)}
          unit={`/ ${data.budget.daily_max_usd} $`}
          state={budgetPct > 80 ? 'warn' : 'default'}
        />
        <KpiCard label="Appels 24h" value={data.budget.last_24h_calls.toString()} />
        <KpiCard label="Coût moyen" value={data.budget.last_24h_avg_cost_usd.toFixed(4)} unit="$" />
        <KpiCard
          label="Circuits ouverts"
          value={openCircuits.length}
          state={openCircuits.length > 0 ? 'warn' : 'success'}
          Icon={openCircuits.length > 0 ? AlertTriangle : CheckCircle}
        />
      </div>

      <Section title="Circuit breakers" description="État des connexions aux services externes">
        <div className="bg-white border border-ducens-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ducens-bg border-b border-ducens-border">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-ducens-muted text-xs uppercase">Service</th>
                <th className="text-left px-4 py-2 font-medium text-ducens-muted text-xs uppercase">État</th>
                <th className="text-left px-4 py-2 font-medium text-ducens-muted text-xs uppercase">Échecs récents</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ducens-border">
              {data.circuits.map((c) => (
                <tr key={c.service}>
                  <td className="px-4 py-2 font-medium">{c.service}</td>
                  <td className="px-4 py-2">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium',
                        c.state === 'closed' && 'bg-emerald-100 text-emerald-900',
                        c.state === 'open' && 'bg-red-100 text-red-900',
                        c.state === 'half_open' && 'bg-orange-100 text-orange-900'
                      )}
                    >
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          c.state === 'closed' && 'bg-emerald-500',
                          c.state === 'open' && 'bg-red-500',
                          c.state === 'half_open' && 'bg-orange-500'
                        )}
                      />
                      {c.state}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-ducens-muted">{c.failure_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Erreurs récentes" description="Dernières 24 heures">
        {data.recent_errors.length === 0 ? (
          <div className="bg-white border border-ducens-border rounded-lg p-6 text-center text-sm text-ducens-muted">
            <CheckCircle className="w-5 h-5 mx-auto mb-2 text-ducens-success" />
            Aucune erreur dans les dernières 24h. Tout roule.
          </div>
        ) : (
          <div className="space-y-2">
            {data.recent_errors.map((err) => (
              <div
                key={err.id}
                className="bg-white border border-ducens-border rounded-md px-4 py-3 flex items-start gap-3"
              >
                <XCircle className="w-4 h-4 text-ducens-danger shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{err.agent_name}</div>
                  <div className="text-xs text-ducens-muted mt-0.5 mb-1">
                    {formatDistanceToNow(new Date(err.timestamp), { locale: fr, addSuffix: true })}
                    {err.model && <> · {err.model}</>}
                  </div>
                  <code className="text-xs text-ducens-danger break-words">{err.error}</code>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="État des services Anthropic" description="Status pages externes">
        <div className="bg-white border border-ducens-border rounded-lg p-4 text-sm">
          <p className="text-ducens-muted mb-2">Vérifier rapidement en cas de problème :</p>
          <ul className="space-y-1.5">
            <li>
              <a
                href="https://status.anthropic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ducens-ink underline underline-offset-2 hover:opacity-70"
              >
                status.anthropic.com
              </a>
            </li>
            <li>
              <a
                href="https://www.shopifystatus.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ducens-ink underline underline-offset-2 hover:opacity-70"
              >
                shopifystatus.com
              </a>
            </li>
            <li>
              <a
                href="https://status.supabase.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ducens-ink underline underline-offset-2 hover:opacity-70"
              >
                status.supabase.com
              </a>
            </li>
          </ul>
        </div>
      </Section>
    </>
  );
}
